import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Checkbox, IconButton, TableCell, TableRow } from '@mui/material';
import React from 'react';
import { useCommonWebTranslation } from '../../hooks';
/**
 * Renders a table row for a data table, including support for:
 * - Multi-selection via checkbox
 * - Custom column rendering
 * - Optional contextual menu trigger.
 *
 * Applies background color changes on hover and selection.
 *
 * @template EntityColumns - The shape of the data row object.
 * @param {Props<EntityColumns>} props - Configuration for the row rendering, including data, columns, and callbacks.
 * @returns {React.ReactElement} The rendered table row element.
 */
export function Row(props) {
    const { t } = useCommonWebTranslation();
    const { id, multiSelect, row, multiSelectedRowIds, onSelectRow, onMultiSelectChange, onMenu } = props;
    const checked = props.multiSelectedRowIds != null && props.multiSelectedRowIds.includes(id);
    return (React.createElement(TableRow, { sx: {
            cursor: 'default',
            '&:hover': {
                backgroundColor: (theme) => (props.selected ? theme.palette.action.selected : theme.palette.action.hover),
            },
            backgroundColor: (theme) => (props.selected ? theme.palette.action.selected : 'default'),
        }, onClick: () => {
            if (onSelectRow) {
                onSelectRow(row);
            }
        }, "data-test": 'row' },
        multiSelect && (React.createElement(TableCell, { padding: 'checkbox' },
            React.createElement(Checkbox, { color: 'primary', checked: checked, onClick: (event) => event.stopPropagation(), onChange: (event) => {
                    if (onMultiSelectChange) {
                        if (event.target.checked) {
                            onMultiSelectChange([...(multiSelectedRowIds !== null && multiSelectedRowIds !== void 0 ? multiSelectedRowIds : []), id]);
                        }
                        else if (multiSelectedRowIds) {
                            onMultiSelectChange(multiSelectedRowIds.filter((selectedRowId) => selectedRowId !== id));
                        }
                    }
                }, "data-test": 'select-checkbox' }))),
        props.columns.map((column) => (React.createElement(TableCell, { key: column.name, "data-test": `${column.name}-column` },
            React.createElement(DataTableCell, { row: props.row, column: column })))),
        onMenu && (React.createElement(TableCell, { sx: { textAlign: 'center' } },
            React.createElement(IconButton, { color: 'inherit', onClick: (event) => {
                    event.stopPropagation();
                    onMenu(event.currentTarget, props.row);
                }, "data-test": 'menu-button', size: 'large', "aria-label": t('common.more') },
                React.createElement(MoreHorizIcon, null))))));
}
/**
 * Renders a cell within a data table.
 * If a custom render function is provided in the column definition, it is used.
 * Otherwise, falls back to rendering the value from the row by column name.
 * Special handling is applied for boolean values.
 *
 * @template T - The type of data row.
 * @param {CellProps<T>} props - Props containing row data and column configuration.
 * @returns {JSX.Element} The rendered cell content.
 */
function DataTableCell(props) {
    const { t } = useCommonWebTranslation();
    if (props.column.render) {
        return React.createElement(React.Fragment, null, props.column.render(props.row, props.column));
    }
    else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const row = props.row;
        const field = props.column.name;
        const value = typeof row === 'object' && field ? row[field] : '';
        if (typeof value === 'boolean') {
            return React.createElement(Box, { sx: { overflow: 'hidden', textOverflow: 'ellipsis' } }, value ? t('common.true') : t('common.false'));
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return React.createElement(Box, { sx: { overflow: 'hidden', textOverflow: 'ellipsis' } }, value);
        }
    }
}
